import { Mail } from "lucide-react";
import { useLanguage } from "@/components/sybrax/LanguageProvider";
import { content } from "@/components/sybrax/i18n";

const handleClick = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const slugFor = (href) => href.replace("#", "");

const Footer = () => {
    const { lang } = useLanguage();
    const c = content[lang].footer;
    const nav = content[lang].nav;

    return (
        <footer
            data-testid="sybrax-footer"
            className="relative border-t border-slate-200/70 dark:border-white/5 mt-12 bg-slate-50/60 dark:bg-transparent"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 sm:py-14">
                <div className="grid md:grid-cols-3 gap-10 md:gap-8">
                    <div>
                        <a
                            href="#hero"
                            data-testid="footer-logo"
                            onClick={(e) => handleClick(e, "#hero")}
                            aria-label="Sybrax — go to top"
                            className="inline-flex"
                        >
                            <img
                                src="/logo-light.png"
                                alt="Sybrax"
                                className="logo-light h-9 w-auto select-none"
                                draggable="false"
                            />
                            <img
                                src="/logo-dark.png"
                                alt="Sybrax"
                                className="logo-dark h-9 w-auto select-none"
                                draggable="false"
                            />
                        </a>
                        <p className="mt-4 text-sm text-slate-600 dark:text-slate-400 leading-relaxed max-w-sm">
                            {c.tagline}
                        </p>
                        <a
                            href="mailto:contact@sybrax.com"
                            data-testid="footer-email-link"
                            className="mt-4 inline-flex items-center gap-2 text-sm text-slate-600 hover:text-cyan-600 dark:text-slate-400 dark:hover:text-cyan-300 transition-colors"
                        >
                            <Mail size={14} strokeWidth={1.8} className="text-cyan-600 dark:text-cyan-400" />
                            contact@sybrax.com
                        </a>
                    </div>

                    <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-6">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400">
                                {c.navigate}
                            </p>
                            <ul className="mt-4 space-y-2.5">
                                {nav.items.slice(0, 3).map((item) => (
                                    <li key={item.href}>
                                        <a
                                            href={item.href}
                                            data-testid={`footer-${slugFor(item.href)}-link`}
                                            onClick={(e) => handleClick(e, item.href)}
                                            className="text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors"
                                        >
                                            {item.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400">
                                {c.company}
                            </p>
                            <ul className="mt-4 space-y-2.5">
                                {nav.items.slice(3).map((item) => (
                                    <li key={item.href}>
                                        <a
                                            href={item.href}
                                            data-testid={`footer-${slugFor(item.href)}-link`}
                                            onClick={(e) => handleClick(e, item.href)}
                                            className="text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors"
                                        >
                                            {item.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400">
                                {c.focus}
                            </p>
                            <ul className="mt-4 space-y-2.5 text-sm text-slate-600 dark:text-slate-400">
                                {c.focusItems.map((f) => (
                                    <li key={f}>{f}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="mt-12 pt-6 border-t border-slate-200/70 dark:border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <p
                        data-testid="footer-copyright"
                        className="text-xs text-slate-500 dark:text-slate-500"
                    >
                        {c.copyright}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-500">
                        {c.closing}
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
